import { Box, Button, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { Interweave } from 'interweave';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ContentCopyOutlined as ContentCopyOutlinedIcon, Publish as PublishIcon } from '@mui/icons-material';
import classes from './SingleVariation.module.scss';
import { SingleVariationProps } from '../../interfaces/props/SingleVariationProps';
import { RouteNames } from '../../enums/RouteNames';
import DateHelper from '../../helpers/date.helper';
import NameHelper from '../../helpers/name.helper';
import { Relations } from '../../enums/Relations';
import GoogleForm from "../../views/google-form/GoogleForm";
import useUser from '../../hooks/useUser';
import VariationContentPiecesService from '../../services/variation-content-pieces.service';
import ConfirmDialog from '../../views/confirm-dialog/ConfirmDialog';
import Notification from '../../views/notification/Notification';
import { NotificationSeverities } from '../../enums/NotificationSeverities';
import { Statuses } from '../../enums/Statuses';

const DEFAULT_SUCCESS_MESSAGE = 'Successfully published!';
const DEFAULT_ERROR_MESSAGE = 'Something went wrong.';

const SingleVariation = ({ variation }: SingleVariationProps) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [status, setStatus] = useState<Statuses>(Statuses.IDLE);
  const [notification, setNotification] = useState<{ isOpen: boolean, severity: NotificationSeverities, message: string }>(
    { isOpen: false, severity: NotificationSeverities.SUCCESS, message: DEFAULT_SUCCESS_MESSAGE }
  );
  const user = useUser();
  const [isVariationPublished, setIsVariationPublished] = useState<boolean>(variation.attributes.isPublished ?? false);
  const checkIfOriginalContentExists = useMemo(() => {
    return null !== variation.attributes.contentPiece?.data;
  }, [variation]);
  const dialogMessage = useMemo(() => `Are you sure you want to send this variation as a draft on ${NameHelper.getNameFromEntity(variation, Relations.SITE)}`, [variation]);

  const onCopyButtonClickHandler = useCallback(() => {
    navigator.clipboard.writeText(`${variation.attributes.headlines} \n ${variation.attributes.content}`);
  }, [variation]);

  const renderAttribute = (label: string, attribute: string) => {
    return (
      attribute && (
        <Typography className={`${classes['original-text']}`}>
          {`${label}${attribute}`}
        </Typography>
      )
    );
  };

  const onPublishClickHandler = () => {
    setStatus(Statuses.PENDING);
    VariationContentPiecesService.publish(variation.id, user?.email ?? '')
      .then((url) => {
        setStatus(Statuses.SUCCESS);
        window.open(url, '_blank', 'noreferrer');
        setNotification({ isOpen: true, severity: NotificationSeverities.SUCCESS, message: DEFAULT_SUCCESS_MESSAGE });
        setIsVariationPublished(true);
      })
      .catch((error: Error) => {
        setStatus(Statuses.ERROR);
        setNotification({ isOpen: true, severity: NotificationSeverities.ERROR, message: error.message ?? DEFAULT_ERROR_MESSAGE })
      })
      .finally(() => {
        setStatus(Statuses.IDLE);
        setIsConfirmModalOpen(false);
      });
  }

  return (
    <>
      <Box className={`${classes['article-box']} p-3 mb-3 d-flex flex-column flex-sm-row justify-content-around`}>
        {renderAttribute('Created for: ', NameHelper.getNameFromEntity(variation, Relations.SITE))}
        <Typography>
          {renderAttribute('Language: ', NameHelper.getNameFromEntity(variation, Relations.LANGUAGE))}
        </Typography>
        {renderAttribute('Type: ', variation.attributes.type)}
        {renderAttribute('Title: ', variation.attributes.headlinesType)}
        {renderAttribute('Length: ', variation.attributes.length)}
        {null !== variation.attributes.structure && (
          <Typography className={`${classes['original-text']} me-3`}>
            Structure: {variation.attributes.structure}
          </Typography>
        )}
      </Box>
      <Box className={`${classes['article-box']} py-3 p-2 p-sm-5`}>
        <Box className='d-flex flex-column flex-lg-row justify-content-between pb-3'>
          {checkIfOriginalContentExists ?
            (<Link
              className={classes.link}
              to={`..${RouteNames.SINGLE_ARTICLE}/${variation.attributes.contentPiece?.data.id}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              <Typography>
                Original Article
              </Typography>
            </Link>
            )
            :
            <Typography>
              Original Article Removed
            </Typography>
          }
          <Typography className={classes['original-text']}>
            {DateHelper.calculateTimeDifferenceFromNow(dayjs(variation.attributes.createdAt))}
          </Typography>
        </Box>
        <Typography
          variant='h4'
          align='left'
          marginBottom={3}
          fontWeight='bold'
        >
          {variation.attributes.headlines}
        </Typography>
        <Box className='overflow-hidden'>
          <Interweave content={variation.attributes.content} />
        </Box>
        <Box className='d-flex flex-column flex-lg-row justify-content-between mt-3 align-items-center'>
          <Typography className={classes['original-text']}>
            Variation author: {variation.attributes.user.data.attributes.email}
          </Typography>
          <Box className='d-flex gap-2 mt-3 mt-lg-0'>
            <Button
              startIcon={<ContentCopyOutlinedIcon />}
              className={classes['enable-button']}
              variant='contained'
              onClick={onCopyButtonClickHandler}
            >
              Copy Variation
            </Button>
            {isVariationPublished ?
              <Button
                className={classes['published-button']}
                variant='contained'
                disabled
              >
                Already Published
              </Button>
              :
              <Button
                startIcon={<PublishIcon />}
                className={classes['publish-button']}
                variant='contained'
                onClick={() => setIsConfirmModalOpen(true)}
              >
                Send as Draft
              </Button>
            }
          </Box>
        </Box>
      </Box>
      {variation.attributes.user.data.attributes.email === user?.email ?
        <Box className={`${classes['form-modal']} w-100 mt-3 p-1`} >
          <GoogleForm />
        </Box> : <></>}
      {isConfirmModalOpen ?
        <ConfirmDialog
          open={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={onPublishClickHandler}
          title={'Publish'}
          message={dialogMessage}
          status={status}
        />
        : null
      }
      <Notification
        open={notification.isOpen}
        onClose={() => setNotification((prevNotification) => ({ ...prevNotification, isOpen: false }))}
        message={notification.message}
        severity={notification.severity}
        position={{ vertical: 'top', horizontal: 'right' }}
      />
    </>
  );
};

export default SingleVariation;
